import './BrandPositionChart.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Skeleton } from 'primereact/skeleton';
import { SELECTED_BRAND_COLOR } from '../../../../../shared/utils/utils';
import { getChartThemeColors, getFirstSeriesPalette } from '../../../../../shared/utils/chartTheme';
import LineChart, { LineChartEffectScatterPoint } from '../../../../../shared/components/chart/line-chart/LineChart';
import { MonitorEventLogResponse } from '../../../../../shared/models/monitor/MonitorEventLogResponse';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { useTranslation } from 'react-i18next';
import { formatEventsForLineChart } from '../../../../../shared/services/monitor/events/EventNameFormatter';
import { getPanelSettings } from '../../../../../shared/services/user/UserService';
import { AnomalyIncident } from '../../../../../shared/models/anomaly-detection/models/AnomalyIncident';
import { AnomalyMetricKey } from '../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import { buildBucketLabelFormatters } from '../../../../../shared/helpers/date/bucketDateLabels';
import { formatAnomalyIncidentTooltip } from '../../../../../shared/helpers/AnomalyIncidentHelpers';

interface BrandPositionChartProps {
  averagePositionData: (string | number)[][];
  brandScoreData: (string | number)[][];
  monitorHeaders: string[];
  eventsData: MonitorEventLogResponse[] | null;
  hasAnyPendingQuestions: boolean;
  hasProjectStatsData: boolean;
  isLoading: boolean;
  brandScoreAnomalies?: AnomalyIncident[];
  averagePositionAnomalies?: AnomalyIncident[];
  /** Active bucket period (DAILY/WEEKLY/MONTHLY) — drives X-axis/tooltip date labels. */
  period?: string | null;
  /** When set, the right (brand score %) Y axis still auto-scales but never exceeds this max (e.g. 100). */
  secondAxisMax?: number;
  /**
   * V2 / NEW_STATS: both series are percentages (Brand Score % as first series, Visibility % as
   * second). Renders the "Brand Score & Visibility" title, formats the left axis as %, both axes
   * 0–100, and does NOT invert the first axis (higher = better, unlike average position).
   */
  percentageMode?: boolean;
  /**
   * V2 / percentageMode only: optional average-position series ([date, averagePosition])
   * plotted on the secondary (right) inverted axis. Hidden by default; the user enables it
   * via the in-header toggle. Lower position = better, so the axis is inverted (best at top).
   */
  averagePositionSecondaryData?: (string | number)[][];
}

const BrandPositionChart: React.FC<BrandPositionChartProps> = ({
  averagePositionData,
  brandScoreData,
  monitorHeaders,
  eventsData,
  hasAnyPendingQuestions,
  hasProjectStatsData,
  isLoading,
  averagePositionAnomalies = [],
  brandScoreAnomalies = [],
  period,
  secondAxisMax,
  percentageMode = false,
  averagePositionSecondaryData,
}) => {
  const { t, i18n } = useTranslation();
  const [displayMarkers, setDisplayMarkers] = useState<boolean | undefined>(undefined);

  // V2 "Brand Score & Visibility": the average-position line is a regular legend series, but it
  // starts DESELECTED (hidden until the user clicks its legend item). It's only offered in
  // percentageMode and only when it actually carries at least one data point.
  const hasAveragePositionData = useMemo(() => {
    if (!percentageMode || !averagePositionSecondaryData) return false;
    return averagePositionSecondaryData
      .slice(1)
      .some((row) => row[1] !== null && row[1] !== undefined);
  }, [percentageMode, averagePositionSecondaryData]);

  // V2 layout: when the average-position series is present, it goes on the FIRST (left) axis —
  // inverted (lower = better) with raw numbers — reusing the first axis's proven inverted-position
  // rendering (same as the V1 chart). Brand Score % + Visibility % then move to the SECOND (right)
  // axis. The position line stays deselected by default (initiallyHiddenSeries).
  const showPositionOnLeft = percentageMode && hasAveragePositionData;
  const chartEvents = useMemo(
    () => formatEventsForLineChart(eventsData),
    [eventsData, i18n.language]
  );

  // Locale + period aware date labels: compact range on the axis, full range
  // (with year) in the tooltip. The matrix first column keeps its raw ISO key/range.
  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  const firstDataEffectScatterPoints = useMemo(
    () => buildAnomalyEffectScatterPoints(
      averagePositionData, averagePositionAnomalies,
      undefined, t('averagePosition'),
      t('averagePositionImproved'), t('averagePositionDeteriorated'),
      'average_position',
    ),
    [averagePositionData, averagePositionAnomalies, t]
  );

  const secondDataEffectScatterPoints = useMemo(
    () => buildAnomalyEffectScatterPoints(
      brandScoreData, brandScoreAnomalies,
      '%', t('brandScore'),
      t('brandScoreImproved'), t('brandScoreDeteriorated'),
      'brand_score',
    ),
    [brandScoreData, brandScoreAnomalies, t]
  );

  const anomalyTooltipFormatter = useCallback(
    (params: any): string => formatAnomalyIncidentTooltip(params, t, dateLabelFormatters.tooltip),
    [t, dateLabelFormatters]
  );

  // In percentageMode, tighten the Y axis to the data instead of always topping out at 100%:
  // max = highest plotted value + 10, capped at 100. Both series (Brand Score %, Visibility %)
  // live in averagePositionData (row 0 = headers, col 0 = date, cols 1+ = values).
  const percentageMaxValue = useMemo(() => {
    if (!percentageMode) return undefined;
    let max = 0;
    for (let rowIndex = 1; rowIndex < averagePositionData.length; rowIndex++) {
      const row = averagePositionData[rowIndex];
      if (!row) continue;
      for (let colIndex = 1; colIndex < row.length; colIndex++) {
        const value = Number(row[colIndex]);
        if (!Number.isNaN(value) && value > max) max = value;
      }
    }
    return Math.min(100, Math.ceil(max) + 10);
  }, [percentageMode, averagePositionData]);

  useEffect(() => {
    let isMounted = true;

    const loadPanelSettings = async (): Promise<void> => {
      try {
        const panelSettings = await getPanelSettings();
        const prefetchedDisplayMarkers = panelSettings?.charts?.brandScoreAndAveragePosition?.displayMarkers;

        if (isMounted) {
          setDisplayMarkers(typeof prefetchedDisplayMarkers === 'boolean' ? prefetchedDisplayMarkers : true);
        }
      } catch (_error) {
        if (isMounted) {
          setDisplayMarkers(true);
        }
      }
    };

    void loadPanelSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t(percentageMode ? 'brandScoreAndVisibility' : 'brandScoreAndAveragePosition')}
          <CustomTooltip
            tooltipText={
              percentageMode ? 'brandScoreAndVisibilityTooltip' : 'brandScoreAndAveragePositionTooltip'
            }
            addMargin={false}
          />
        </div>
      </div>
      {!hasProjectStatsData && !hasAnyPendingQuestions && !isLoading ? (
        <div className="stats-box stats-box--without-margin stats-box--chart">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : !hasProjectStatsData || isLoading ? (
        <div className="stats-box stats-box--without-margin stats-box--chart">
          <div className="stats-box__content">
            <Skeleton className="skeleton--chart">
              <span />
            </Skeleton>
          </div>
        </div>
      ) : (
        <div className="stats-box stats-box--without-margin stats-box--chart">
          <div className="stats-box__content">
            <LineChart
              // V2 (showPositionOnLeft): average position on the LEFT (first) axis — inverted, raw
              // numbers. Otherwise firstData holds the % series (V2 with no position data) or the
              // V1 average-position series.
              firstData={
                showPositionOnLeft && averagePositionSecondaryData
                  ? averagePositionSecondaryData
                  : averagePositionData
              }
              firstColorPalette={
                showPositionOnLeft
                  ? [getChartThemeColors().neutralLine]
                  : percentageMode
                  ? [getFirstSeriesPalette()[3], SELECTED_BRAND_COLOR]
                  : [getFirstSeriesPalette()[3]]
              }
              // V2 (showPositionOnLeft): Brand Score % + Visibility % move to the RIGHT (second) axis.
              // V1: brand score % on the right. V2 without position data: no second axis.
              secondData={
                showPositionOnLeft
                  ? averagePositionData
                  : percentageMode
                  ? undefined
                  : brandScoreData
              }
              secondColorPalette={
                showPositionOnLeft
                  ? [getFirstSeriesPalette()[3], SELECTED_BRAND_COLOR]
                  : [SELECTED_BRAND_COLOR]
              }
              // Position is listed in the legend but DESELECTED by default (initiallyHiddenSeries).
              headers={
                showPositionOnLeft ? [t('averagePosition'), ...monitorHeaders] : monitorHeaders
              }
              initiallyHiddenSeries={showPositionOnLeft ? [t('averagePosition')] : undefined}
              // Average position lives on the left (firstData) axis but should appear LAST in the
              // legend, after Brand Score & Visibility — so render the secondData group first.
              secondDataLegendFirst={showPositionOnLeft}
              onLegendClick={() => {}}
              // Left-align the legend only in the V2 "Brand Score & Visibility" (percentageMode)
              // chart; the legacy chart keeps the spread-out (SPACE_BETWEEN) layout as before.
              legendType={percentageMode ? 'FLEX_START' : 'SPACE_BETWEEN'}
              // Left axis is inverted for average position (V2 with position, or V1); the % left axis
              // (V2 without position) is not inverted. The right (%) axis is never inverted.
              inverseY={showPositionOnLeft || !percentageMode}
              formatYAsPercentage={percentageMode && !showPositionOnLeft}
              {...(showPositionOnLeft
                ? { minValue: 1 }
                : percentageMode
                ? { minValue: 0, maxValue: percentageMaxValue }
                : {})}
              marksData={chartEvents}
              panelSettingsConfig="brandScoreAndAveragePosition"
              prefetchedDisplayMarkers={displayMarkers}
              firstDataEffectScatterPoints={percentageMode ? undefined : firstDataEffectScatterPoints}
              secondDataEffectScatterPoints={percentageMode ? undefined : secondDataEffectScatterPoints}
              dateAxisFormatter={dateLabelFormatters.axis}
              dateTooltipFormatter={dateLabelFormatters.tooltip}
              effectScatterTooltipFormatter={anomalyTooltipFormatter}
              secondAxisMax={showPositionOnLeft ? 100 : percentageMode ? undefined : secondAxisMax}
            />
          </div>
        </div>
      )}
    </div>
    </>
  );
};

const buildAnomalyEffectScatterPoints = (
  matrix: (string | number)[][],
  anomalies: AnomalyIncident[],
  valueUnit?: string,
  metricName?: string,
  verbPositive?: string,
  verbNegative?: string,
  metric?: AnomalyMetricKey,
): LineChartEffectScatterPoint[] => {
  const dateColumn = 0;
  const valueColumn = 1;
  if (matrix.length <= 1 || !anomalies.length) {
    return [];
  }

  const dateToValue = new Map<string, number>();
  for (let rowIndex = 1; rowIndex < matrix.length; rowIndex++) {
    const row = matrix[rowIndex];
    if (!row || row.length <= valueColumn) {
      continue;
    }
    const dateKey = String(row[dateColumn]);
    const value = Number(row[valueColumn]);
    dateToValue.set(dateKey, value);
  }

  return anomalies.flatMap((anomaly) => {
    const value = dateToValue.get(anomaly.endDate);
    if (value === undefined) {
      return [];
    }
    return [
      {
        date: anomaly.endDate,
        value,
        extra: {
          ...anomaly,
          ...(valueUnit ? { valueUnit } : {}),
          ...(metric ? { metric } : {}),
          ...(metricName ? { metricName } : {}),
          ...(verbPositive ? { verbPositive } : {}),
          ...(verbNegative ? { verbNegative } : {}),
        },
      },
    ];
  });
};

export default BrandPositionChart;
